import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getDevtoolsAuthToken } from "../../devtools/utils/devtoolsAuth";

const emptyProcess = {
  slug: "",
  name: "",
  system_name: "",
  backup_type: "manual",
  environment: "",
  source_path_or_db: "",
  destination_type: "",
  destination_path: "",
  frequency: "",
  retention_rule: "",
  automation_status: "manual",
  owner: "",
  notes: "",
  restore_method: "",
  runs: [],
};

const emptyGitState = {
  project: null,
  backend: { status: null, error: "" },
  frontend: { status: null, error: "" },
  shared: { status: null, error: "" },
};

const emptyDbBackupConfig = {
  id: null,
  project_id: "",
  label: "",
  is_backup_required: true,
  database_name: "",
  backup_root_path: "",
  backup_subfolder: "",
  filename_pattern: "",
  retention_days: 7,
  copy_off_server_required: false,
  off_server_target_path: "",
  pre_deployment_backup_required: true,
  last_verified_at: "",
  notes: "",
};

const emptyDbBackupStatus = {
  configured_database_name: "",
  configured_output_folder: "",
  latest_backup_file_path: "",
  latest_run_status: "never_run",
  latest_run_timestamp: "",
  latest_run: null,
};

const backupTypeOptions = ["manual", "database", "file-copy", "off-server-copy", "code-sync", "config", "restore-test"];
const automationOptions = ["manual", "planned", "automated"];
const runStatusOptions = ["success", "failed", "partial", "skipped"];
const scopeOptions = [
  { value: "backend", label: "Backend Repo" },
  { value: "frontend", label: "Frontend Repo" },
  { value: "shared", label: "Shared Repo" },
];

function buildHeaders(includeJson = false) {
  const token = getDevtoolsAuthToken();
  const headers = {};
  if (includeJson) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function TreeNode({ node, depth = 0 }) {
  const isDirectory = node?.type === "directory";
  return (
    <Box sx={{ pl: depth * 1.5, py: 0.25 }}>
      <Typography variant="body2" color={isDirectory ? "text.primary" : "text.secondary"}>
        {isDirectory ? "📁" : "📄"} {node?.name}
      </Typography>
      {isDirectory && Array.isArray(node?.children)
        ? node.children.map((child) => (
            <TreeNode key={`${node.name}-${child.name}`} node={child} depth={depth + 1} />
          ))
        : null}
    </Box>
  );
}

function FileSelectionList({ title, items, selectedPaths, onToggle, color = "text.secondary" }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      {items?.length ? (
        <Stack spacing={0.5}>
          {items.map((item) => (
            <FormControlLabel
              key={item.path}
              control={
                <Checkbox
                  size="small"
                  checked={selectedPaths.includes(item.path)}
                  onChange={() => onToggle(item.path)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" color={color} sx={{ wordBreak: "break-word" }}>{item.path}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.code}</Typography>
                </Box>
              }
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">None</Typography>
      )}
    </Box>
  );
}

function RepoStatusCard({ title, payload, selectedPaths, onToggle }) {
  const repoStatus = payload?.status;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>{title}</Typography>
      {payload?.error ? <Alert severity="error" sx={{ mb: 1 }}>{payload.error}</Alert> : null}
      {repoStatus ? (
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}><strong>Repo:</strong> {repoStatus.repo_path}</Typography>
            <Typography variant="body2"><strong>Branch:</strong> {repoStatus.branch || "(none)"}</Typography>
            <Typography variant="body2"><strong>HEAD:</strong> {repoStatus.head || "(unknown)"}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}><strong>Remote:</strong> {repoStatus.remote || "(none)"}</Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
            <Chip label={`Modified ${repoStatus.tracked_modified?.length || 0}`} size="small" />
            <Chip label={`Deleted ${repoStatus.tracked_deleted?.length || 0}`} size="small" />
            <Chip label={`Untracked ${repoStatus.untracked?.length || 0}`} size="small" />
            <Chip label={repoStatus.clean ? "Clean" : "Dirty"} color={repoStatus.clean ? "success" : "warning"} size="small" />
          </Stack>
          <Divider />
          <FileSelectionList
            title="Untracked files available to add"
            items={repoStatus.untracked || []}
            selectedPaths={selectedPaths}
            onToggle={onToggle}
          />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">No status loaded yet.</Typography>
      )}
    </Paper>
  );
}

export default function BackupsPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [processes, setProcesses] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [draft, setDraft] = useState(emptyProcess);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [initializing, setInitializing] = useState(false);
  const [runForm, setRunForm] = useState({ run_type: "manual", status: "success", artifact_path: "", notes: "" });

  const [projects, setProjects] = useState([]);
  const [dbBackupProjects, setDbBackupProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("ebay_sandbox");
  const [selectedDbProjectId, setSelectedDbProjectId] = useState("ebay_sandbox");
  const [selectedScope, setSelectedScope] = useState("backend");
  const [gitState, setGitState] = useState(emptyGitState);
  const [gitBusy, setGitBusy] = useState(false);
  const [gitError, setGitError] = useState("");
  const [gitOutput, setGitOutput] = useState("");
  const [selectedUntrackedPaths, setSelectedUntrackedPaths] = useState([]);
  const [commitMessage, setCommitMessage] = useState("Backup snapshot from DEV IDE");
  const [pushNotes, setPushNotes] = useState("Manual backup push triggered from DEV IDE Backups tab.");

  const [dbBackupConfig, setDbBackupConfig] = useState(emptyDbBackupConfig);
  const [dbBackupStatus, setDbBackupStatus] = useState(emptyDbBackupStatus);
  const [dbBackupHistory, setDbBackupHistory] = useState([]);
  const [dbBackupBusy, setDbBackupBusy] = useState(false);
  const [dbBackupError, setDbBackupError] = useState("");
  const [dbBackupMessage, setDbBackupMessage] = useState("");

  const selectedSummary = useMemo(
    () => processes.find((item) => item.slug === selectedSlug) || null,
    [processes, selectedSlug]
  );

  const activeRepoStatus = useMemo(() => {
    if (selectedScope === "frontend") return gitState.frontend?.status;
    if (selectedScope === "shared") return gitState.shared?.status;
    return gitState.backend?.status;
  }, [gitState, selectedScope]);

  const loadTree = async () => {
    const res = await fetch("/api/docs/backups/tree", { headers: buildHeaders(false) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || "Failed to load backup tree.");
    setTree(data.tree || []);
  };



const loadDatabaseBackupConfig = async (projectId = selectedDbProjectId) => {
  if (!projectId) return;
  const res = await fetch(`/api/docs/backups/projects/${encodeURIComponent(projectId)}/database-config`, {
    headers: buildHeaders(false),
  });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || "Failed to load DB backup config.");
  setDbBackupConfig({ ...emptyDbBackupConfig, ...(data.config || {}), project_id: projectId });
};

const loadDatabaseBackupStatus = async (projectId = selectedDbProjectId) => {
  if (!projectId) return;
  const res = await fetch(`/api/docs/backups/projects/${encodeURIComponent(projectId)}/database-status`, {
    headers: buildHeaders(false),
  });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || "Failed to load DB backup status.");
  setDbBackupStatus({ ...emptyDbBackupStatus, ...(data.status || {}) });
  setDbBackupHistory(data.history || []);
};

  const loadProjects = async (preferredProjectId = "") => {
    const res = await fetch("/api/docs/backups/projects", { headers: buildHeaders(false) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || "Failed to load backup projects.");
    const next = data.projects || [];
    setProjects(next);
    const fallbackId = preferredProjectId || selectedProjectId || next[0]?.project_id || "";
    if (fallbackId) setSelectedProjectId(fallbackId);
  };


  const loadDbBackupProjects = async (preferredProjectId = "") => {
    const res = await fetch("/api/docs/backups/database-projects", { headers: buildHeaders(false) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || "Failed to load DB backup projects.");
    const next = data.projects || [];
    setDbBackupProjects(next);
    const fallbackId = preferredProjectId || selectedDbProjectId || next[0]?.project_id || "";
    if (fallbackId) setSelectedDbProjectId(fallbackId);
  };

  const loadGitStatus = async (projectId = selectedProjectId) => {
    if (!projectId) return;
    setGitBusy(true);
    setGitError("");
    try {
      const res = await fetch(`/api/docs/backups/projects/${encodeURIComponent(projectId)}/status`, {
        headers: buildHeaders(false),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to load git status.");
      setGitState(data.result || emptyGitState);
      setSelectedUntrackedPaths([]);
    } catch (err) {
      setGitError(err.message || "Failed to load git status.");
    } finally {
      setGitBusy(false);
    }
  };

  const loadProcess = async (slug, listOverride = null) => {
    setSelectedSlug(slug);
    const res = await fetch(`/api/docs/backups/processes/${encodeURIComponent(slug)}`, {
      headers: buildHeaders(false),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || "Failed to load backup process.");
    const process = data.process || emptyProcess;
    setDraft({ ...emptyProcess, ...process, runs: process.runs || [] });
    if (listOverride) {
      setProcesses(listOverride);
    }
  };

  const loadProcesses = async (preferSlug = "") => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/docs/backups/processes", { headers: buildHeaders(false) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to load backup processes.");
      const next = data.processes || [];
      setProcesses(next);
      const nextSlug = preferSlug || (selectedSlug && next.some((item) => item.slug === selectedSlug) ? selectedSlug : next[0]?.slug || "");
      if (nextSlug) {
        await loadProcess(nextSlug, next);
      } else {
        setSelectedSlug("");
        setDraft(emptyProcess);
      }
      await loadTree();
      await loadProjects(selectedProjectId);
      await loadDbBackupProjects(selectedDbProjectId);
      const activeProjectId = selectedProjectId || next[0]?.project_id || "ebay_sandbox";
      const activeDbProjectId = selectedDbProjectId || "ebay_sandbox";
      await loadGitStatus(activeProjectId);
      await loadDatabaseBackupConfig(activeDbProjectId);
      await loadDatabaseBackupStatus(activeDbProjectId);
    } catch (err) {
      setError(err.message || "Failed to load backup processes.");
    } finally {
      setLoading(false);
    }
  };

  const initBackups = async () => {
    setInitializing(true);
    setError("");
    try {
      const res = await fetch("/api/docs/backups/init", {
        method: "POST",
        headers: buildHeaders(true),
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to initialize backups.");
      setTree(data.storage?.tree || []);
      await loadProcesses(selectedSlug);
    } catch (err) {
      setError(err.message || "Failed to initialize backups.");
    } finally {
      setInitializing(false);
    }
  };

  useEffect(() => {
    initBackups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateNew = () => {
    setSelectedSlug("");
    setDraft({
      ...emptyProcess,
      name: "New Backup Process",
      notes: "Describe the backup process and what it protects.",
    });
    setRunForm({ run_type: "manual", status: "success", artifact_path: "", notes: "" });
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = { ...draft };
      delete payload.runs;
      const res = await fetch(
        selectedSlug ? `/api/docs/backups/processes/${encodeURIComponent(selectedSlug)}` : "/api/docs/backups/processes",
        {
          method: selectedSlug ? "PUT" : "POST",
          headers: buildHeaders(true),
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to save backup process.");
      await loadProcesses(data.process?.slug || selectedSlug);
    } catch (err) {
      setError(err.message || "Failed to save backup process.");
    } finally {
      setSaving(false);
    }
  };

  const handleRecordRun = async () => {
    if (!selectedSlug) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`/api/docs/backups/processes/${encodeURIComponent(selectedSlug)}/runs`, {
        method: "POST",
        headers: buildHeaders(true),
        body: JSON.stringify(runForm),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to record backup run.");
      setDraft({ ...emptyProcess, ...(data.process || {}), runs: data.process?.runs || [] });
      setRunForm({ run_type: "manual", status: "success", artifact_path: "", notes: "" });
      await loadProcesses(selectedSlug);
    } catch (err) {
      setError(err.message || "Failed to record backup run.");
    } finally {
      setSaving(false);
    }
  };

  const runGitAction = async (action, payload = {}, options = {}) => {
    if (!selectedProjectId) return;
    setGitBusy(true);
    setGitError("");
    try {
      const res = await fetch(`/api/docs/backups/projects/${encodeURIComponent(selectedProjectId)}/actions/${action}`, {
        method: "POST",
        headers: buildHeaders(true),
        body: JSON.stringify({ scope: selectedScope, ...payload }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Failed to run ${action}.`);
      if (data.result?.git_output) setGitOutput(data.result.git_output);
      if (action === "push") {
        const commitHash = data.result?.commit_hash || "";
        setRunForm((prev) => ({
          ...prev,
          run_type: "manual_git_push",
          status: "success",
          artifact_path: commitHash,
          notes: `Manual ${selectedScope} push to backup branch completed.`,
        }));
      }
      if (!options.skipReload) await loadGitStatus(selectedProjectId);
    } catch (err) {
      setGitError(err.message || `Failed to run ${action}.`);
    } finally {
      setGitBusy(false);
    }
  };

  const toggleUntrackedPath = (path) => {
    setSelectedUntrackedPaths((prev) => (
      prev.includes(path) ? prev.filter((item) => item !== path) : [...prev, path]
    ));
  };

  const handleGitProjectChange = async (event) => {
    const nextProjectId = event.target.value;
    setSelectedProjectId(nextProjectId);
    setSelectedUntrackedPaths([]);
    await loadGitStatus(nextProjectId);
  };

  const handleDbProjectChange = async (event) => {
    const nextProjectId = event.target.value;
    setSelectedDbProjectId(nextProjectId);
    setDbBackupError("");
    setDbBackupMessage("");
    await loadDatabaseBackupConfig(nextProjectId);
    await loadDatabaseBackupStatus(nextProjectId);
  };

  const handleSaveDbBackupConfig = async () => {
    if (!selectedDbProjectId) return;
    setDbBackupBusy(true);
    setDbBackupError("");
    setDbBackupMessage("");
    try {
      const res = await fetch(`/api/docs/backups/projects/${encodeURIComponent(selectedDbProjectId)}/database-config`, {
        method: "PUT",
        headers: buildHeaders(true),
        body: JSON.stringify(dbBackupConfig),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to save DB backup config.");
      setDbBackupConfig({ ...emptyDbBackupConfig, ...(data.config || {}), project_id: selectedDbProjectId });
      setDbBackupMessage("DB backup config saved.");
      await loadDatabaseBackupStatus(selectedDbProjectId);
    } catch (err) {
      setDbBackupError(err.message || "Failed to save DB backup config.");
    } finally {
      setDbBackupBusy(false);
    }
  };

  const handleRefreshDbBackupStatus = async () => {
    if (!selectedDbProjectId) return;
    setDbBackupBusy(true);
    setDbBackupError("");
    setDbBackupMessage("");
    try {
      await loadDatabaseBackupConfig(selectedDbProjectId);
      await loadDatabaseBackupStatus(selectedDbProjectId);
      setDbBackupMessage("DB backup status refreshed.");
    } catch (err) {
      setDbBackupError(err.message || "Failed to refresh DB backup status.");
    } finally {
      setDbBackupBusy(false);
    }
  };

  const handleRunManualDbBackup = async () => {
    if (!selectedDbProjectId) return;
    setDbBackupBusy(true);
    setDbBackupError("");
    setDbBackupMessage("");
    try {
      const res = await fetch(`/api/docs/backups/projects/${encodeURIComponent(selectedDbProjectId)}/actions/run-manual-db-backup`, {
        method: "POST",
        headers: buildHeaders(true),
        body: JSON.stringify({ notes: "Manual DB backup triggered from DEV IDE Backups tab." }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to run manual DB backup.");
      if (data.result?.status_card) {
        setDbBackupStatus({ ...emptyDbBackupStatus, ...(data.result.status_card || {}) });
      }
      if (Array.isArray(data.result?.history)) {
        setDbBackupHistory(data.result.history || []);
      } else {
        await loadDatabaseBackupStatus(selectedDbProjectId);
      }
      setDbBackupMessage(`Manual DB backup created: ${data.result?.output_file_path || "completed"}`);
    } catch (err) {
      setDbBackupError(err.message || "Failed to run manual DB backup.");
    } finally {
      setDbBackupBusy(false);
    }
  };

  const handleDownloadDbBackupRun = (runId) => {
    if (!selectedDbProjectId || !runId) return;
    const token = getDevtoolsAuthToken();
    const params = new URLSearchParams();
    if (token) params.set("auth_token", token);
    const query = params.toString();
    const url = `/api/docs/backups/projects/${encodeURIComponent(selectedDbProjectId)}/database-runs/${encodeURIComponent(runId)}/download${query ? `?${query}` : ""}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        gap: 2,
        height: "100%",
        p: { xs: 1, sm: 2 },
        alignItems: "stretch",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", lg: 320 },
          minWidth: { xs: 0, lg: 280 },
          p: { xs: 1.5, sm: 2 },
          overflow: "auto",
          maxHeight: { xs: "none", lg: "100%" },
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h6">Backup Processes</Typography>
          <Button size="small" variant="contained" onClick={handleCreateNew} fullWidth={isMobile}>New</Button>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          <Button size="small" variant="outlined" onClick={initBackups} disabled={initializing} fullWidth={isMobile}>
            {initializing ? "Initializing..." : "Init Storage"}
          </Button>
        </Stack>

        {loading || initializing ? <CircularProgress size={22} /> : null}
        {error ? <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography> : null}

        <List dense sx={{ maxHeight: { xs: 260, lg: "none" }, overflowY: "auto" }}>
          {processes.map((process) => (
            <ListItemButton
              key={process.slug}
              selected={selectedSlug === process.slug}
              onClick={() => loadProcess(process.slug)}
              alignItems="flex-start"
            >
              <ListItemText
                primary={process.name}
                secondary={`${process.automation_status || "manual"} • ${process.last_result || "never_run"}`}
              />
            </ListItemButton>
          ))}
          {!loading && processes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No backup processes yet.</Typography>
          ) : null}
        </List>
      </Paper>

      <Paper sx={{ flex: 1, p: { xs: 1.5, sm: 2 }, overflow: "auto", minWidth: 0 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">Backup Process Editor</Typography>
            <Typography variant="body2" color="text.secondary">
              Define the backup process, what it protects, and how its runs are recorded.
            </Typography>
          </Box>
          <Button variant="contained" onClick={handleSave} disabled={saving || !draft.name.trim()} fullWidth={isMobile}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Stack>

        <Stack spacing={2}>
          <TextField label="Name" value={draft.name} onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))} fullWidth />
          <TextField label="System / App" value={draft.system_name} onChange={(e) => setDraft((prev) => ({ ...prev, system_name: e.target.value }))} fullWidth />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField select label="Backup type" value={draft.backup_type} onChange={(e) => setDraft((prev) => ({ ...prev, backup_type: e.target.value }))} fullWidth>
              {backupTypeOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
            <TextField label="Environment" value={draft.environment} onChange={(e) => setDraft((prev) => ({ ...prev, environment: e.target.value }))} fullWidth />
          </Stack>
          <TextField label="Source path / DB" value={draft.source_path_or_db} onChange={(e) => setDraft((prev) => ({ ...prev, source_path_or_db: e.target.value }))} fullWidth />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField label="Destination type" value={draft.destination_type} onChange={(e) => setDraft((prev) => ({ ...prev, destination_type: e.target.value }))} fullWidth />
            <TextField label="Destination path" value={draft.destination_path} onChange={(e) => setDraft((prev) => ({ ...prev, destination_path: e.target.value }))} fullWidth />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField label="Frequency" value={draft.frequency} onChange={(e) => setDraft((prev) => ({ ...prev, frequency: e.target.value }))} fullWidth />
            <TextField label="Retention rule" value={draft.retention_rule} onChange={(e) => setDraft((prev) => ({ ...prev, retention_rule: e.target.value }))} fullWidth />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField select label="Automation status" value={draft.automation_status} onChange={(e) => setDraft((prev) => ({ ...prev, automation_status: e.target.value }))} fullWidth>
              {automationOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
            <TextField label="Owner" value={draft.owner} onChange={(e) => setDraft((prev) => ({ ...prev, owner: e.target.value }))} fullWidth />
          </Stack>
          <TextField label="Notes" value={draft.notes} onChange={(e) => setDraft((prev) => ({ ...prev, notes: e.target.value }))} fullWidth multiline minRows={4} />
          <TextField label="Restore method" value={draft.restore_method} onChange={(e) => setDraft((prev) => ({ ...prev, restore_method: e.target.value }))} fullWidth multiline minRows={3} />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Manual Git Backup Workflow</Typography>
            <Typography variant="body2" color="text.secondary">
              Review repo state, stage intentional changes, then commit and push to the configured backup branch.
            </Typography>
          </Box>

          {gitError ? <Alert severity="error">{gitError}</Alert> : null}
          {gitOutput ? <Alert severity="info">{gitOutput}</Alert> : null}

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="backup-project-select-label">Project</InputLabel>
              <Select
                labelId="backup-project-select-label"
                label="Project"
                value={selectedProjectId}
                onChange={handleGitProjectChange}
              >
                {projects.map((project) => (
                  <MenuItem key={project.project_id} value={project.project_id}>
                    {project.label || project.project_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="backup-scope-select-label">Repo Scope</InputLabel>
              <Select
                labelId="backup-scope-select-label"
                label="Repo Scope"
                value={selectedScope}
                onChange={(event) => {
                  setSelectedScope(event.target.value);
                  setSelectedUntrackedPaths([]);
                }}
              >
                {scopeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
            <Button variant="outlined" disabled={gitBusy || !selectedProjectId} onClick={() => loadGitStatus(selectedProjectId)} fullWidth={isMobile}>
              {gitBusy ? "Refreshing..." : "Refresh Git Status"}
            </Button>
            <Button variant="outlined" disabled={gitBusy || !selectedProjectId} onClick={() => runGitAction("checkout-backup")} fullWidth={isMobile}>Checkout / Create Backup Branch</Button>
            <Button variant="outlined" disabled={gitBusy || !selectedProjectId} onClick={() => runGitAction("stage-tracked")} fullWidth={isMobile}>Stage Tracked Changes</Button>
            <Button
              variant="outlined"
              disabled={gitBusy || !selectedProjectId || selectedUntrackedPaths.length === 0}
              onClick={() => runGitAction("stage-selected", { paths: selectedUntrackedPaths })}
              fullWidth={isMobile}
            >
              Stage Selected New Files
            </Button>
          </Stack>

          <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <RepoStatusCard
                title="Backend Repo"
                payload={gitState.backend}
                selectedPaths={selectedScope === "backend" ? selectedUntrackedPaths : []}
                onToggle={selectedScope === "backend" ? toggleUntrackedPath : () => {}}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <RepoStatusCard
                title="Frontend Repo"
                payload={gitState.frontend}
                selectedPaths={selectedScope === "frontend" ? selectedUntrackedPaths : []}
                onToggle={selectedScope === "frontend" ? toggleUntrackedPath : () => {}}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <RepoStatusCard
                title="Shared Repo"
                payload={gitState.shared}
                selectedPaths={selectedScope === "shared" ? selectedUntrackedPaths : []}
                onToggle={selectedScope === "shared" ? toggleUntrackedPath : () => {}}
              />
            </Box>
          </Stack>

          <TextField
            label="Commit message"
            value={commitMessage}
            onChange={(e) => setCommitMessage(e.target.value)}
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              disabled={gitBusy || !selectedProjectId || !commitMessage.trim()}
              onClick={() => runGitAction("commit", { message: commitMessage })}
              fullWidth={isMobile}
            >
              Commit Current Staged State
            </Button>
          </Stack>

          <TextField
            label="Push notes"
            value={pushNotes}
            onChange={(e) => setPushNotes(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              color="success"
              disabled={gitBusy || !selectedProjectId}
              onClick={() => runGitAction("push", { notes: pushNotes })}
              fullWidth={isMobile}
            >
              Push Backup Branch
            </Button>
          </Stack>


  <Typography variant="caption" color="text.secondary">
    Active repo path: {activeRepoStatus?.repo_path || "(load status first)"}
  </Typography>
</Stack>

<Divider sx={{ my: 3 }} />

<Stack spacing={2}>
  <Box>
    <Typography variant="h6">Manual Database Backup Workflow</Typography>
    <Typography variant="body2" color="text.secondary">
      Store database backup settings in the DB and run an on-demand mysqldump from the Backups tab.
    </Typography>
  </Box>

  {dbBackupError ? <Alert severity="error">{dbBackupError}</Alert> : null}
  {dbBackupMessage ? <Alert severity="success">{dbBackupMessage}</Alert> : null}

  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
    <FormControl fullWidth>
      <InputLabel id="db-backup-project-select-label">Project</InputLabel>
      <Select
        labelId="db-backup-project-select-label"
        label="Project"
        value={selectedDbProjectId}
        onChange={handleDbProjectChange}
      >
        {dbBackupProjects.map((project) => (
          <MenuItem key={project.project_id} value={project.project_id}>
            {project.label || project.project_id}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <TextField
      label="Database name"
      value={dbBackupConfig.database_name}
      onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, database_name: e.target.value }))}
      fullWidth
    />
  </Stack>

  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
    <TextField
      label="Backup root path"
      value={dbBackupConfig.backup_root_path}
      onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, backup_root_path: e.target.value }))}
      fullWidth
    />
    <TextField
      label="Backup subfolder"
      value={dbBackupConfig.backup_subfolder}
      onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, backup_subfolder: e.target.value }))}
      fullWidth
    />
  </Stack>

  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
    <TextField
      label="Filename pattern"
      value={dbBackupConfig.filename_pattern}
      onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, filename_pattern: e.target.value }))}
      fullWidth
    />
    <TextField
      label="Retention days"
      type="number"
      value={dbBackupConfig.retention_days}
      onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, retention_days: e.target.value }))}
      fullWidth
    />
  </Stack>

  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
    <FormControlLabel
      control={
        <Checkbox
          checked={Boolean(dbBackupConfig.copy_off_server_required)}
          onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, copy_off_server_required: e.target.checked }))}
        />
      }
      label="Copy off-server required"
    />
    <TextField
      label="Off-server target path"
      value={dbBackupConfig.off_server_target_path}
      onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, off_server_target_path: e.target.value }))}
      fullWidth
    />
  </Stack>

  <FormControlLabel
    control={
      <Checkbox
        checked={Boolean(dbBackupConfig.pre_deployment_backup_required)}
        onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, pre_deployment_backup_required: e.target.checked }))}
      />
    }
    label="Pre-deployment backup required"
  />

  <TextField
    label="Notes"
    value={dbBackupConfig.notes}
    onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, notes: e.target.value }))}
    fullWidth
    multiline
    minRows={3}
  />

  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
    <Button variant="contained" disabled={dbBackupBusy || !selectedDbProjectId} onClick={handleSaveDbBackupConfig} fullWidth={isMobile}>
      {dbBackupBusy ? "Working..." : "SAVE DB BACKUP CONFIG"}
    </Button>
    <Button variant="outlined" disabled={dbBackupBusy || !selectedDbProjectId} onClick={handleRefreshDbBackupStatus} fullWidth={isMobile}>
      REFRESH DB BACKUP STATUS
    </Button>
    <Button variant="contained" color="success" disabled={dbBackupBusy || !selectedDbProjectId} onClick={handleRunManualDbBackup} fullWidth={isMobile}>
      RUN MANUAL DB BACKUP
    </Button>
  </Stack>

  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle1" sx={{ mb: 1 }}>DB Backup Status</Typography>
    <Stack spacing={0.75}>
      <Typography variant="body2"><strong>Configured database name:</strong> {dbBackupStatus.configured_database_name || "(not set)"}</Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}><strong>Configured output folder:</strong> {dbBackupStatus.configured_output_folder || "(not set)"}</Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}><strong>Latest backup file path:</strong> {dbBackupStatus.latest_backup_file_path || "(none yet)"}</Typography>
      <Typography variant="body2"><strong>Latest run status:</strong> {dbBackupStatus.latest_run_status || "never_run"}</Typography>
      <Typography variant="body2"><strong>Latest run timestamp:</strong> {dbBackupStatus.latest_run_timestamp || "(none yet)"}</Typography>
      <Box sx={{ pt: 1 }}>
        <Button fullWidth={isMobile}
          variant="outlined"
          size="small"
          disabled={!dbBackupStatus.latest_run?.id}
          onClick={() => handleDownloadDbBackupRun(dbBackupStatus.latest_run?.id)}
        >
          DOWNLOAD LATEST BACKUP FILE
        </Button>
      </Box>
    </Stack>
  </Paper>
</Stack>
      </Paper>


<Paper sx={{ width: { xs: "100%", xl: 380 }, minWidth: { xs: 0, xl: 340 }, p: { xs: 1.5, sm: 2 }, overflow: "auto" }}>
  <Typography variant="h6" sx={{ mb: 1 }}>DB Backup Run History</Typography>
  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
    Latest manual database backup runs for the selected project.
  </Typography>

  <List dense sx={{ mb: 2 }}>
    {dbBackupHistory.map((run) => (
      <Box key={`db-run-${run.id}`} sx={{ mb: 1.5 }}>
        <ListItemText
          primary={`${run.status || "unknown"} • ${run.run_type || "manual_db_backup"}`}
          secondary={`${run.started_at || ""}${run.output_file_path ? ` • ${run.output_file_path}` : ""}`}
        />
        {run.file_size_bytes ? (
          <Typography variant="body2" color="text.secondary">File size: {run.file_size_bytes} bytes</Typography>
        ) : null}
        {run.notes ? <Typography variant="body2" color="text.secondary">{run.notes}</Typography> : null}
        <Box sx={{ pt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!run.id}
            onClick={() => handleDownloadDbBackupRun(run.id)}
            fullWidth={isMobile}
          >
            Download SQL File
          </Button>
        </Box>
        <Divider sx={{ mt: 1 }} />
      </Box>
    ))}
    {dbBackupHistory.length === 0 ? (
      <Typography variant="body2" color="text.secondary">No DB backup runs recorded yet.</Typography>
    ) : null}
  </List>

  <Divider sx={{ my: 2 }} />
  <Typography variant="h6" sx={{ mb: 1 }}>Run History</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedSummary ? `Manual history for ${selectedSummary.name}` : "Select a backup process to view and record runs."}
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <TextField label="Run type" value={runForm.run_type} onChange={(e) => setRunForm((prev) => ({ ...prev, run_type: e.target.value }))} fullWidth />
          <TextField select label="Status" value={runForm.status} onChange={(e) => setRunForm((prev) => ({ ...prev, status: e.target.value }))} fullWidth>
            {runStatusOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
          <TextField label="Artifact path / commit hash" value={runForm.artifact_path} onChange={(e) => setRunForm((prev) => ({ ...prev, artifact_path: e.target.value }))} fullWidth />
          <TextField label="Notes" value={runForm.notes} onChange={(e) => setRunForm((prev) => ({ ...prev, notes: e.target.value }))} fullWidth multiline minRows={3} />
          <Button variant="outlined" onClick={handleRecordRun} disabled={!selectedSlug || saving} fullWidth={isMobile}>Record Manual Run</Button>
        </Stack>

        <List dense>
          {(draft.runs || []).map((run) => (
            <Box key={run.run_id} sx={{ mb: 1.5 }}>
              <ListItemText
                primary={`${run.status || "unknown"} • ${run.run_type || "manual"}`}
                secondary={`${run.run_at || ""}${run.artifact_path ? ` • ${run.artifact_path}` : ""}`}
              />
              {run.notes ? <Typography variant="body2" color="text.secondary">{run.notes}</Typography> : null}
              <Divider sx={{ mt: 1 }} />
            </Box>
          ))}
          {(draft.runs || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No run history recorded yet.</Typography>
          ) : null}
        </List>

        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>Backup Storage Tree</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Backup process definition files are stored on the server and shown here.
        </Typography>
        {isMobile && tree.length > 0 ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Long paths wrap on mobile. Scroll within this card for the full storage tree.
          </Alert>
        ) : null}
        {tree.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No backup storage folders found yet.</Typography>
        ) : (
          tree.map((node) => <TreeNode key={node.name} node={node} />)
        )}
      </Paper>
    </Box>
  );
}
